import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from '../utils/axios';
import { keepAlive, logout as logoutUrl } from '../utils/urls';
import {
  getIdleTimeoutSeconds,
  setIdleTimeoutSeconds,
  clearSession,
  setLogoutReason,
  ACTIVITY_EVENT,
} from '../utils/session';

const WARNING_LEAD_MS = 60 * 1000;  // show the modal ~1 min before idle expiry
const CHECK_INTERVAL_MS = 5 * 1000; // how often we evaluate inactivity
const ACTIVITY_THROTTLE_MS = 1000;  // cap how often DOM activity is processed

const now = () => Date.now();
const hasSession = () => !!localStorage.getItem('authTokens');

/**
 * Client-side idle-session manager, mounted once inside the authenticated shell.
 *
 * Mirrors the backend's server-side idle window (idle_timeout_seconds): warns the
 * user ~1 min before it elapses and logs them out if they don't respond. Real API
 * traffic (ACTIVITY_EVENT from axios) and user DOM activity both count as
 * activity; an active-but-no-API user is kept alive with a throttled background
 * keep-alive so their server window never silently expires.
 */
export default function useIdleTimeout() {
  const navigate = useNavigate();
  const location = useLocation();

  const lastActivityRef = useRef(now());    // last user/API activity
  const lastServerSlideRef = useRef(now());  // last time the server window slid
  const warningShownRef = useRef(false);     // a modal is currently open
  const keepAliveInFlightRef = useRef(false);

  // Keep navigate reachable from listeners without re-registering them.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Treat every route change as activity.
  useEffect(() => {
    lastActivityRef.current = now();
  }, [location.pathname]);

  useEffect(() => {
    // --- helpers -----------------------------------------------------------
    const idleLogout = (reason) => {
      setLogoutReason(reason);
      clearSession();
      Swal.close();
      navigateRef.current('/login');
    };

    const manualLogout = async () => {
      try {
        await axios.post(logoutUrl);
      } catch (err) {
        /* best-effort */
      }
      clearSession();
      navigateRef.current('/login');
    };

    const sendKeepAlive = async () => {
      if (keepAliveInFlightRef.current) return null;
      keepAliveInFlightRef.current = true;
      try {
        const res = await axios.post(keepAlive);
        lastServerSlideRef.current = now();
        if (res?.data?.idle_timeout_seconds != null) {
          setIdleTimeoutSeconds(res.data.idle_timeout_seconds);
        }
        return res;
      } catch (err) {
        // A 401 here is already turned into a hard logout by the axios
        // interceptor; other failures are non-fatal.
        return null;
      } finally {
        keepAliveInFlightRef.current = false;
      }
    };

    const showWarning = (idleMs) => {
      if (warningShownRef.current) return;
      warningShownRef.current = true;

      const remaining = Math.max(1000, idleMs - (now() - lastActivityRef.current));
      let countdownInterval;

      Swal.fire({
        title: 'Still working?',
        html: 'You will be logged out due to inactivity in <b></b> seconds.',
        icon: 'warning',
        timer: remaining,
        timerProgressBar: true,
        showCancelButton: true,
        confirmButtonText: 'Stay logged in',
        cancelButtonText: 'Log out',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          const b = Swal.getHtmlContainer()?.querySelector('b');
          const render = () => {
            if (b) b.textContent = String(Math.ceil((Swal.getTimerLeft() || 0) / 1000));
          };
          render();
          countdownInterval = setInterval(render, 250);
        },
        willClose: () => clearInterval(countdownInterval),
      }).then(async (result) => {
        warningShownRef.current = false;

        if (result.isConfirmed) {
          // Positive click: the user must never be logged out here. Slide the
          // client window optimistically and ping keep-alive to slide the server
          // window too (a 401 is handled by the interceptor, not here).
          lastActivityRef.current = now();
          lastServerSlideRef.current = now();
          await sendKeepAlive();
          return;
        }

        // "Log out" or the timer fully elapsed.
        manualLogout();
      });
    };

    // --- activity listeners ------------------------------------------------
    let lastHandled = 0;
    const onUserActivity = () => {
      const t = now();
      if (t - lastHandled < ACTIVITY_THROTTLE_MS) return;
      lastHandled = t;
      lastActivityRef.current = t;

      if (!hasSession()) return;

      // Keep an active-but-no-API user's server window alive: once the server
      // window is halfway to expiry with no API traffic, quietly ping keep-alive.
      const idleMs = getIdleTimeoutSeconds() * 1000;
      if (t - lastServerSlideRef.current >= idleMs / 2) {
        sendKeepAlive();
      }
    };

    const onApiActivity = () => {
      lastActivityRef.current = now();
      lastServerSlideRef.current = now();
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach((e) => window.addEventListener(e, onUserActivity, { passive: true }));
    window.addEventListener(ACTIVITY_EVENT, onApiActivity);

    // --- poll loop ---------------------------------------------------------
    const interval = setInterval(() => {
      if (!hasSession() || warningShownRef.current) return;

      const idleMs = getIdleTimeoutSeconds() * 1000;
      const elapsed = now() - lastActivityRef.current;

      if (elapsed >= idleMs) {
        idleLogout('session_idle');
      } else if (elapsed >= idleMs - WARNING_LEAD_MS) {
        showWarning(idleMs);
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onUserActivity));
      window.removeEventListener(ACTIVITY_EVENT, onApiActivity);
      clearInterval(interval);
    };
  }, []);
}
